import { cx } from "@emotion/css";
import { useContext } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Redirect, useHistory } from "react-router-dom";

import { FullScreenNotificationLayout } from "@/layouts/FullScreenNotificationLayout/FullScreenNotificationLayout";
import { levelCongratulationsContext } from "@/modules/levels/contexts/LevelCongratulationsContext";
import { authenticatedRouting } from "@/modules/router/routings/authenticatedRouting";
import { FullScreenNotificationBox } from "@/modules/theme/components/FullScreenNotificationBox";
import { LevelProgress } from "@/modules/theme/components/LevelProgress/LevelProgress";
import { Loader } from "@/modules/theme/components/Loader/Loader";
import { PageMeta } from "@/modules/theme/components/PageMeta";
import { useTheme } from "@/modules/theme/hooks/useTheme";

import { createLevelCongratulationsPageStyles } from "./createLevelCongratulationsPageStyles";
import { ReactComponent as Star1 } from "./icons/star1.svg";
import { ReactComponent as Star10 } from "./icons/star10.svg";
import { ReactComponent as Star11 } from "./icons/star11.svg";
import { ReactComponent as Star12 } from "./icons/star12.svg";
import { ReactComponent as Star13 } from "./icons/star13.svg";
import { ReactComponent as Star14 } from "./icons/star14.svg";
import { ReactComponent as Star2 } from "./icons/star2.svg";
import { ReactComponent as Star3 } from "./icons/star3.svg";
import { ReactComponent as Star4 } from "./icons/star4.svg";
import { ReactComponent as Star5 } from "./icons/star5.svg";
import { ReactComponent as Star6 } from "./icons/star6.svg";
import { ReactComponent as Star7 } from "./icons/star7.svg";
import { ReactComponent as Star8 } from "./icons/star8.svg";
import { ReactComponent as Star9 } from "./icons/star9.svg";

const translationNamespace = "pages/LevelCongratulationsPage";

export const LevelCongratulationsPage = () => {
  const { t } = useTranslation(translationNamespace);
  const classes = createLevelCongratulationsPageStyles(useTheme());
  const history = useHistory();
  const {
    acknowledgeLevel,
    currentUnacknowledgedLevel,
    areStatusesPending,
  } = useContext(levelCongratulationsContext);

  if (!currentUnacknowledgedLevel) {
    return <Redirect to={authenticatedRouting.buildUrl("tasks")} />;
  }

  return (
    <FullScreenNotificationLayout
      onSubmit={() => {
        if (!areStatusesPending) {
          if (currentUnacknowledgedLevel?.group) {
            acknowledgeLevel(
              currentUnacknowledgedLevel.id,
              currentUnacknowledgedLevel.group.id
            );
          } else {
            history.push(authenticatedRouting.buildUrl("tasks"));
          }
        }
      }}
      variant="success"
    >
      <PageMeta title={t("Congratulations!")} />
      {currentUnacknowledgedLevel && (
        <FullScreenNotificationBox
          textClassName={classes.text}
          title={t("Congratulations!")}
          text={
            <Trans
              t={t}
              i18nKey="You've reached level: {{levelName}} <br /> in group: {{groupName}}.<br /> Keep it up!"
              values={{
                levelName: currentUnacknowledgedLevel.levelName,
                groupName: currentUnacknowledgedLevel.group?.name,
              }}
            />
          }
        >
          <LevelProgress
            active
            done
            level={currentUnacknowledgedLevel.order}
            progress={100}
          />
          {areStatusesPending && (
            <div className={classes.loaderWrapper}>
              <Loader size={30} variant="primary" />
            </div>
          )}
        </FullScreenNotificationBox>
      )}
      <Star1 className={cx(classes.star, classes.star1)} />
      <Star2 className={cx(classes.star, classes.star2)} />
      <Star3 className={cx(classes.star, classes.star3)} />
      <Star4 className={cx(classes.star, classes.star4)} />
      <Star5 className={cx(classes.star, classes.star5)} />
      <Star6 className={cx(classes.star, classes.star6)} />
      <Star7 className={cx(classes.star, classes.star7)} />
      <Star8 className={cx(classes.star, classes.star8)} />
      <Star9 className={cx(classes.star, classes.star9)} />
      <Star10 className={cx(classes.star, classes.star10)} />
      <Star11 className={cx(classes.star, classes.star11)} />
      <Star12 className={cx(classes.star, classes.star12)} />
      <Star13 className={cx(classes.star, classes.star13)} />
      <Star14 className={cx(classes.star, classes.star14)} />
    </FullScreenNotificationLayout>
  );
};
